import { useEffect } from "react";
import { Link, NavLink, Route, Routes, useLocation } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { getLocale, setLocale, t, type Locale } from "../i18n";
import { GlPage } from "./modules/Gl";
import { ArPage, ApPage, BiPage, ExPage } from "./modules/Ledgers";
import { PoPage, EntitiesPage } from "./modules/PoEntities";
import { TaxPage } from "./modules/Tax";
import { ComplianceHub } from "./Compliance";
import { CEOPage, CFOPage } from "./Dashboards";
import { Logo, MacroHome, ModuleHub, SoonPage, PILLS } from "./ModuleHome";
import { WorkCenterPage } from "./WorkCenter";

const LOCALES: [Locale, string][] = [["en", "🇬🇧"], ["fr", "🇫🇷"], ["es", "🇪🇸"]];

// Reset scroll to the top on every route change (module switch, drill-in, etc.).
function ScrollToTop() {
  const { pathname, search } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname, search]);
  return null;
}

export function Shell() {
  const { signOut, identity } = useAuth();
  const locale = getLocale();
  const tenant = identity?.tenant_id || "";
  const role = identity?.roles?.[0] || "";
  return (
    <div className="shell">
      <ScrollToTop />
      <header className="hdr">
        <div className="hdr-inner">
          <Link to="/" className="brand">
            <Logo size={38} />
            <span className="words">
              <span className="wordmark">{t("app.title")}</span>
              <span className="tagline">{t("app.system")}</span>
            </span>
          </Link>
          <nav className="hdr-nav">
            <NavLink to="/" end>{t("nav.home")}</NavLink>
            <NavLink to="/workcenter">WorkCenter</NavLink>
          </nav>
          <nav className="hdr-mods">
            {PILLS.map((m) => (
              <NavLink key={m.code} to={m.to} className={m.cls}>{m.code}</NavLink>
            ))}
          </nav>
          <nav className="hdr-nav hdr-aux">
            <a href="https://aicos.lopettia.ai/tour/" target="_blank" rel="noopener noreferrer">{t("Tour")}</a>
            <a href="https://aicos.lopettia.ai/manuals/" target="_blank" rel="noopener noreferrer">{t("Manual")}</a>
          </nav>
          <span className="hdr-spacer" />
          {(tenant || role) && <span className="ident-pill">{tenant}{tenant && role ? " · " : ""}{role}</span>}
          <div className="lang">
            {LOCALES.map(([l, flag]) => (
              <button
                key={l}
                className={locale === l ? "active" : ""}
                title={l.toUpperCase()}
                onClick={() => { setLocale(l); location.reload(); }}
              >{flag} {l.toUpperCase()}</button>
            ))}
          </div>
          <button className="sign-out" onClick={signOut}>{t("auth.signOut")}</button>
        </div>
      </header>
      <main className="content">
        <Routes>
          <Route path="/" element={<MacroHome />} />
          <Route path="/m/:code" element={<ModuleHub />} />
          <Route path="/workcenter" element={<WorkCenterPage />} />
          <Route path="/soon" element={<SoonPage />} />
          <Route path="/gl" element={<GlPage />} />
          <Route path="/ceo" element={<CEOPage />} />
          <Route path="/cfo" element={<CFOPage />} />
          <Route path="/ar" element={<ArPage />} />
          <Route path="/ap" element={<ApPage />} />
          <Route path="/bi" element={<BiPage />} />
          <Route path="/po" element={<PoPage />} />
          <Route path="/ex" element={<ExPage />} />
          <Route path="/tax" element={<TaxPage />} />
          <Route path="/compliance" element={<ComplianceHub />} />
          <Route path="/compliance/:code" element={<ComplianceHub />} />
          <Route path="/entities" element={<EntitiesPage />} />
        </Routes>
      </main>
    </div>
  );
}
